import React, { useState, useEffect } from 'react';
import { billingService } from '../../services/dataService';
import { todayInTz } from '../../utils/tzUtils';

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

const DEFAULT_SETTINGS = {
  gst_enabled: false,
  gst_percent: 5,
  gstin: '',
  service_charge_enabled: false,
  service_charge_percent: 10,
  discount_enabled: false,
  discount_type: 'NONE',
  discount_value: 0,
  invoice_prefix: 'INV',
  thermal_paper_width: 58,
  print_logo: false,
  print_tagline: false,
};

const inputStyle = {
  width: '100%', padding: '12px 15px',
  border: '2px solid #e5e7eb', borderRadius: '10px',
  fontSize: '1rem', outline: 'none',
  transition: 'border-color 0.2s', boxSizing: 'border-box',
};

const Toggle = ({ active, onToggle }) => (
  <div
    onClick={onToggle}
    style={{
      position: 'relative', display: 'inline-block',
      width: '52px', height: '28px',
      background: active ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : '#d1d5db',
      borderRadius: '14px', cursor: 'pointer',
      transition: 'background 0.3s', flexShrink: 0,
    }}
  >
    <div style={{
      position: 'absolute', top: '3px',
      left: active ? '27px' : '3px',
      width: '22px', height: '22px',
      background: 'white', borderRadius: '50%',
      transition: 'left 0.3s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
    }} />
  </div>
);

const Section = ({ title, children }) => (
  <div style={{
    background: 'rgba(255,255,255,0.95)', borderRadius: '16px',
    padding: '28px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
    border: '1px solid rgba(0,0,0,0.06)',
  }}>
    <h2 style={{ margin: '0 0 20px 0', fontSize: '1.15rem', fontWeight: '700', color: '#333' }}>{title}</h2>
    {children}
  </div>
);

const ToggleRow = ({ label, description, active, onToggle }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
    <div>
      <div style={{ fontWeight: '600', color: '#333', marginBottom: '4px' }}>{label}</div>
      {description && <div style={{ fontSize: '0.82rem', color: '#888' }}>{description}</div>}
    </div>
    <Toggle active={active} onToggle={onToggle} />
  </div>
);

const BLE_PRINTER_KEY = 'menuron_ble_printer';
const CASH_DRAWER_KEY = 'menuron_cash_drawer_enabled';

export function isCashDrawerEnabled() {
  try { return localStorage.getItem(CASH_DRAWER_KEY) === 'true'; } catch (_) { return false; }
}

const BillingSettings = ({ restaurant, onSaved }) => {
  const [form, setForm] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [errors, setErrors] = useState({});
  const [terminalId, setTerminalId] = useState('');
  const [savedPrinter, setSavedPrinter] = useState(null);
  const [scanStatus, setScanStatus] = useState('');
  const [cashDrawerEnabled, setCashDrawerEnabled] = useState(isCashDrawerEnabled);

  useEffect(() => {
    if (window.menuronDesktop?.isDesktop) {
      window.menuronDesktop.printer.bleGetSaved().then(info => {
        if (info) setSavedPrinter(info);
      }).catch(() => {});
    } else {
      try {
        const raw = localStorage.getItem(BLE_PRINTER_KEY);
        if (raw) setSavedPrinter(JSON.parse(raw));
      } catch (_) {}
    }
  }, []);

  useEffect(() => {
    if (window.menuronDesktop?.isDesktop) {
      window.menuronDesktop.terminal.getConfig().then(cfg => {
        if (cfg?.terminal_id) setTerminalId(cfg.terminal_id);
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!restaurant?.id) return;
    loadSettings();
  }, [restaurant?.id]);

  const loadSettings = async () => {
    setLoading(true);
    const { data, error } = await billingService.getSettings(restaurant.id);
    if (error) {
      setErrors({ _load: 'Failed to load billing settings.' });
    } else if (data) {
      setForm({
        gst_enabled: data.gst_enabled ?? false,
        gst_percent: data.gst_percent ?? 5,
        gstin: data.gstin ?? '',
        service_charge_enabled: data.service_charge_enabled ?? false,
        service_charge_percent: data.service_charge_percent ?? 10,
        discount_enabled: data.discount_enabled ?? false,
        discount_type: data.discount_type ?? 'NONE',
        discount_value: data.discount_value ?? 0,
        invoice_prefix: data.invoice_prefix ?? 'INV',
        thermal_paper_width: data.thermal_paper_width ?? 58,
        print_logo: data.print_logo ?? false,
        print_tagline: data.print_tagline ?? false,
      });
    }
    setLoading(false);
  };

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setSuccess('');
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.gst_enabled) {
      const v = parseFloat(form.gst_percent);
      if (isNaN(v) || v <= 0 || v > 28) e.gst_percent = 'GST % must be between 0.01 and 28.';
      const g = form.gstin.trim().toUpperCase();
      if (!g || !GSTIN_REGEX.test(g)) e.gstin = 'Enter a valid 15-character GSTIN.';
    }
    if (form.service_charge_enabled) {
      const v = parseFloat(form.service_charge_percent);
      if (isNaN(v) || v <= 0 || v > 20) e.service_charge_percent = 'Service charge % must be between 0.01 and 20.';
    }
    if (form.discount_enabled) {
      if (form.discount_type === 'NONE') {
        e.discount_type = 'Select a discount type.';
      } else if (form.discount_type === 'PERCENT') {
        const v = parseFloat(form.discount_value);
        if (isNaN(v) || v <= 0 || v > 100) e.discount_value = 'Discount % must be between 0.01 and 100.';
      } else if (form.discount_type === 'FIXED') {
        const v = parseFloat(form.discount_value);
        if (isNaN(v) || v < 0) e.discount_value = 'Discount amount must be ≥ 0.';
      }
    }
    if (!form.invoice_prefix.trim()) e.invoice_prefix = 'Invoice prefix cannot be empty.';
    return e;
  };

  const handleConnectPrinter = async () => {
    if (!window.menuronDesktop?.isDesktop) return;
    setScanStatus('scanning');
    try {
      const info = await window.menuronDesktop.printer.bleScan();
      setSavedPrinter(info);
      setScanStatus('');
    } catch (e) {
      setScanStatus(e.message === 'Cancelled' ? '' : (e.message || 'Scan failed'));
    }
  };

  const handleToggleCashDrawer = () => {
    const next = !cashDrawerEnabled;
    setCashDrawerEnabled(next);
    try { localStorage.setItem(CASH_DRAWER_KEY, String(next)); } catch (_) {}
  };

  const handleForgetPrinter = () => {
    if (window.menuronDesktop?.isDesktop) {
      window.menuronDesktop.printer.bleClearSaved();
    } else {
      localStorage.removeItem(BLE_PRINTER_KEY);
    }
    setSavedPrinter(null);
    setScanStatus('');
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setSaving(true); setSuccess(''); setErrors({});

    const payload = {
      gst_enabled: form.gst_enabled,
      gst_percent: form.gst_enabled ? parseFloat(form.gst_percent) : 0,
      gstin: form.gst_enabled ? form.gstin.trim().toUpperCase() : null,
      service_charge_enabled: form.service_charge_enabled,
      service_charge_percent: form.service_charge_enabled ? parseFloat(form.service_charge_percent) : 0,
      discount_enabled: form.discount_enabled,
      discount_type: form.discount_enabled ? form.discount_type : 'NONE',
      discount_value: form.discount_enabled ? parseFloat(form.discount_value) || 0 : 0,
      pricing_mode: 'EXCLUSIVE',
      invoice_prefix: form.invoice_prefix.trim().toUpperCase(),
      thermal_paper_width: form.thermal_paper_width,
      print_logo: form.print_logo,
      print_tagline: form.print_tagline,
    };

    const { data: saved, error } = await billingService.saveSettings(restaurant.id, payload);
    setSaving(false);
    if (error) setErrors({ _save: 'Failed to save. ' + (error.message || '') });
    else { setSuccess('Billing settings saved successfully.'); if (onSaved && saved) onSaved(saved); }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>Loading billing settings...</div>;

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '30px 20px' }}>
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ margin: '0 0 6px 0', fontSize: '2rem', fontWeight: '700', color: '#333' }}>Billing Settings</h1>
        <p style={{ margin: '0 0 10px 0', color: '#666' }}>Configure taxes, charges, and default discount applied to counter orders.</p>
      </div>

      {(success || errors._load || errors._save) && (
        <div style={{
          background: success ? '#d4edda' : '#fee2e2',
          border: `1px solid ${success ? '#c3e6cb' : '#fecaca'}`,
          borderRadius: '10px', padding: '14px 18px', marginBottom: '24px',
          color: success ? '#155724' : '#dc2626', fontSize: '0.95rem',
        }}>
          {success || errors._load || errors._save}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

        {/* GST */}
        <Section title="GST / Tax">
          <ToggleRow
            label="Enable GST"
            description="Apply GST on all counter orders"
            active={form.gst_enabled}
            onToggle={() => set('gst_enabled', !form.gst_enabled)}
          />
          {form.gst_enabled && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontWeight: '600', color: '#333', marginBottom: '8px', fontSize: '0.9rem' }}>GST Rate (%) <span style={{ color: '#dc2626' }}>*</span></label>
                <input
                  type="number" min="0.01" max="28" step="0.5" value={form.gst_percent}
                  onChange={e => set('gst_percent', e.target.value)}
                  style={{ ...inputStyle, borderColor: errors.gst_percent ? '#dc2626' : '#e5e7eb' }}
                  onFocus={e => e.target.style.borderColor = '#FF6B00'}
                  onBlur={e => e.target.style.borderColor = errors.gst_percent ? '#dc2626' : '#e5e7eb'}
                />
                {errors.gst_percent && <p style={{ margin: '5px 0 0', color: '#dc2626', fontSize: '0.82rem' }}>{errors.gst_percent}</p>}
                <p style={{ margin: '6px 0 0', color: '#888', fontSize: '0.82rem' }}>Mode: Exclusive (added on top of item price). Max 28%.</p>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '600', color: '#333', marginBottom: '8px', fontSize: '0.9rem' }}>GSTIN <span style={{ color: '#dc2626' }}>*</span></label>
                <input
                  type="text" placeholder="e.g. 29ABCDE1234F1Z5" maxLength={15}
                  value={form.gstin}
                  onChange={e => set('gstin', e.target.value.trim().toUpperCase())}
                  style={{ ...inputStyle, letterSpacing: '0.05em', borderColor: errors.gstin ? '#dc2626' : '#e5e7eb' }}
                  onFocus={e => e.target.style.borderColor = '#FF6B00'}
                  onBlur={e => e.target.style.borderColor = errors.gstin ? '#dc2626' : '#e5e7eb'}
                />
                {errors.gstin
                  ? <p style={{ margin: '5px 0 0', color: '#dc2626', fontSize: '0.82rem' }}>{errors.gstin}</p>
                  : <p style={{ margin: '6px 0 0', color: '#888', fontSize: '0.82rem' }}>15-character GST Identification Number. Required when GST is enabled.</p>
                }
              </div>
            </div>
          )}
        </Section>

        {/* Service Charge */}
        <Section title="Service Charge">
          <ToggleRow
            label="Enable Service Charge"
            description="Add a service charge to counter orders"
            active={form.service_charge_enabled}
            onToggle={() => set('service_charge_enabled', !form.service_charge_enabled)}
          />
          {form.service_charge_enabled && (
            <div>
              <label style={{ display: 'block', fontWeight: '600', color: '#333', marginBottom: '8px', fontSize: '0.9rem' }}>Service Charge Rate (%) <span style={{ color: '#dc2626' }}>*</span></label>
              <input
                type="number" min="0.01" max="20" step="0.5" value={form.service_charge_percent}
                onChange={e => set('service_charge_percent', e.target.value)}
                style={{ ...inputStyle, borderColor: errors.service_charge_percent ? '#dc2626' : '#e5e7eb' }}
                onFocus={e => e.target.style.borderColor = '#FF6B00'}
                onBlur={e => e.target.style.borderColor = errors.service_charge_percent ? '#dc2626' : '#e5e7eb'}
              />
              {errors.service_charge_percent && <p style={{ margin: '5px 0 0', color: '#dc2626', fontSize: '0.82rem' }}>{errors.service_charge_percent}</p>}
              <p style={{ margin: '6px 0 0', color: '#888', fontSize: '0.82rem' }}>Max 20%.</p>
            </div>
          )}
        </Section>

        {/* Default Discount */}
        <Section title="Default Discount">
          <ToggleRow
            label="Enable Default Discount"
            description="Pre-fill a discount on every bill (manager can adjust per order)"
            active={form.discount_enabled}
            onToggle={() => {
              const next = !form.discount_enabled;
              setForm(prev => ({ ...prev, discount_enabled: next, discount_type: next ? (prev.discount_type === 'NONE' ? 'PERCENT' : prev.discount_type) : 'NONE', discount_value: next ? prev.discount_value : 0 }));
              setSuccess(''); setErrors({});
            }}
          />
          {form.discount_enabled && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontWeight: '600', color: '#333', marginBottom: '8px', fontSize: '0.9rem' }}>Discount Type <span style={{ color: '#dc2626' }}>*</span></label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {[{ id: 'PERCENT', label: '% Percent' }, { id: 'FIXED', label: '₹ Fixed' }].map(t => (
                    <button
                      key={t.id}
                      onClick={() => set('discount_type', t.id)}
                      style={{
                        flex: 1, padding: '10px', borderRadius: '10px', fontWeight: '600', fontSize: '0.9rem', cursor: 'pointer',
                        background: form.discount_type === t.id ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : 'transparent',
                        color: form.discount_type === t.id ? 'white' : '#555',
                        border: form.discount_type === t.id ? 'none' : '2px solid #e5e7eb',
                      }}
                    >{t.label}</button>
                  ))}
                </div>
                {errors.discount_type && <p style={{ margin: '5px 0 0', color: '#dc2626', fontSize: '0.82rem' }}>{errors.discount_type}</p>}
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '600', color: '#333', marginBottom: '8px', fontSize: '0.9rem' }}>
                  {form.discount_type === 'PERCENT' ? 'Discount %' : 'Discount Amount (₹)'} <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="number" min="0" step={form.discount_type === 'PERCENT' ? '0.5' : '1'} value={form.discount_value}
                  max={form.discount_type === 'PERCENT' ? 100 : undefined}
                  onChange={e => set('discount_value', e.target.value)}
                  style={{ ...inputStyle, borderColor: errors.discount_value ? '#dc2626' : '#e5e7eb' }}
                  onFocus={e => e.target.style.borderColor = '#FF6B00'}
                  onBlur={e => e.target.style.borderColor = errors.discount_value ? '#dc2626' : '#e5e7eb'}
                />
                {errors.discount_value && <p style={{ margin: '5px 0 0', color: '#dc2626', fontSize: '0.82rem' }}>{errors.discount_value}</p>}
              </div>
            </div>
          )}
        </Section>

        {/* Thermal Paper */}
        <Section title="Thermal Printer">
          {/* Connected printer */}
          <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: '600', color: '#333', fontSize: '0.9rem', marginBottom: '4px' }}>Connected Printer</div>
                {savedPrinter ? (
                  <>
                    <div style={{ fontWeight: '700', color: '#111', fontSize: '0.95rem' }}>{savedPrinter.name || savedPrinter.id}</div>
                    <div style={{ color: '#888', fontSize: '0.78rem', marginTop: '2px' }}>{savedPrinter.id}</div>
                  </>
                ) : (
                  <div style={{ color: '#888', fontSize: '0.85rem' }}>No printer saved</div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {window.menuronDesktop?.isDesktop && (
                  <button
                    onClick={handleConnectPrinter}
                    disabled={scanStatus === 'scanning'}
                    style={{ background: scanStatus === 'scanning' ? '#e5e7eb' : 'linear-gradient(135deg, #FF4444, #FF6B00)', border: 'none', borderRadius: '8px', padding: '7px 14px', fontSize: '0.85rem', fontWeight: '600', color: scanStatus === 'scanning' ? '#9ca3af' : 'white', cursor: scanStatus === 'scanning' ? 'not-allowed' : 'pointer' }}
                  >{scanStatus === 'scanning' ? 'Scanning…' : savedPrinter ? 'Change' : 'Connect'}</button>
                )}
                {savedPrinter && (
                  <button
                    onClick={handleForgetPrinter}
                    style={{ background: 'transparent', border: '1.5px solid #e5e7eb', borderRadius: '8px', padding: '7px 14px', fontSize: '0.85rem', fontWeight: '600', color: '#dc2626', cursor: 'pointer' }}
                  >Forget</button>
                )}
              </div>
            </div>
            {scanStatus && scanStatus !== 'scanning' && (
              <div style={{ marginTop: '8px', color: '#dc2626', fontSize: '0.82rem' }}>{scanStatus}</div>
            )}
          </div>
          <div style={{ marginBottom: '8px', fontWeight: '600', color: '#333', fontSize: '0.9rem' }}>Paper Width</div>
          <div style={{ display: 'flex', gap: '10px' }}>
            {[{ val: 80, label: '80 mm' }, { val: 58, label: '58 mm' }].map(opt => (
              <button
                key={opt.val}
                onClick={() => set('thermal_paper_width', opt.val)}
                style={{
                  flex: 1, padding: '10px', borderRadius: '10px', fontWeight: '600', fontSize: '0.9rem', cursor: 'pointer',
                  background: form.thermal_paper_width === opt.val ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : 'transparent',
                  color: form.thermal_paper_width === opt.val ? 'white' : '#555',
                  border: form.thermal_paper_width === opt.val ? 'none' : '2px solid #e5e7eb',
                }}
              >{opt.label}</button>
            ))}
          </div>
          <p style={{ margin: '10px 0 0', color: '#888', fontSize: '0.82rem' }}>
            Match this to your printer's paper roll size. Affects the print layout only.
          </p>
        </Section>

        {/* Cash Drawer */}
        <Section title="Cash Drawer">
          <ToggleRow
            label="Auto-open on cash payment"
            description="Opens the cash drawer automatically when an invoice is generated with cash payment. Drawer must be connected to your BLE printer via the RJ11 port."
            active={cashDrawerEnabled}
            onToggle={handleToggleCashDrawer}
          />
          {cashDrawerEnabled && (
            <p style={{ margin: '0', color: '#888', fontSize: '0.82rem' }}>
              Drawer will open whenever a cash payment is completed — both on auto-paid invoices and when printing the receipt.
            </p>
          )}
        </Section>

        {/* Receipt Branding */}
        <Section title="Receipt Branding">
          <ToggleRow
            label="Print Logo"
            description="Show your restaurant logo at the top of printed receipts. Upload your logo in Profile settings."
            active={form.print_logo}
            onToggle={() => set('print_logo', !form.print_logo)}
          />
          <ToggleRow
            label="Print Tagline"
            description="Show your restaurant tagline below the name on receipts. Set your tagline in Profile settings."
            active={form.print_tagline}
            onToggle={() => set('print_tagline', !form.print_tagline)}
          />
        </Section>

        {/* Invoice Settings */}
        <Section title="Invoice Numbering">
          <div>
            <label style={{ display: 'block', fontWeight: '600', color: '#333', marginBottom: '8px', fontSize: '0.9rem' }}>Invoice Prefix</label>
            <input
              type="text" placeholder="e.g. INV" maxLength={10}
              value={form.invoice_prefix}
              onChange={e => set('invoice_prefix', e.target.value.toUpperCase())}
              style={{ ...inputStyle, borderColor: errors.invoice_prefix ? '#dc2626' : '#e5e7eb' }}
              onFocus={e => e.target.style.borderColor = '#FF6B00'}
              onBlur={e => e.target.style.borderColor = errors.invoice_prefix ? '#dc2626' : '#e5e7eb'}
            />
            {errors.invoice_prefix && <p style={{ margin: '5px 0 0', color: '#dc2626', fontSize: '0.82rem' }}>{errors.invoice_prefix}</p>}
            <p style={{ margin: '10px 0 0', color: '#888', fontSize: '0.82rem' }}>
              Invoice numbers are auto-generated as <strong>{(() => { const [y,m,d] = todayInTz(restaurant?.timezone || 'Asia/Kolkata').split('-'); return `${(form.invoice_prefix || 'INV').toUpperCase()}:${d}${m}${y}0001`; })()}</strong> and reset daily at midnight ({restaurant?.timezone || 'Asia/Kolkata'}). Only the prefix can be customised.
            </p>
          </div>
        </Section>
      </div>

      {terminalId && (
        <div style={{ marginTop: '28px', background: '#f8f9fa', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '16px 20px' }}>
          <p style={{ margin: '0 0 6px 0', fontSize: '0.78rem', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Terminal ID</p>
          <p style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.95rem', color: '#333', letterSpacing: '0.05em' }}>{terminalId}</p>
          <p style={{ margin: '6px 0 0', fontSize: '0.78rem', color: '#aaa' }}>Unique identifier for this machine. Share this with support if needed.</p>
        </div>
      )}

      <div style={{ marginTop: '28px', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            background: saving ? '#ccc' : 'linear-gradient(135deg, #FF4444, #FF6B00)',
            color: 'white', border: 'none', borderRadius: '12px',
            padding: '14px 32px', fontSize: '1rem', fontWeight: '700',
            cursor: saving ? 'not-allowed' : 'pointer',
            boxShadow: saving ? 'none' : '0 4px 15px rgba(255,68,68,0.3)',
          }}
        >
          {saving ? 'Saving...' : 'Save Billing Settings'}
        </button>
      </div>
    </div>
  );
};

export default BillingSettings;
